import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Reveal } from "../Reveal";

const programs = [
  {
    label: "Virksomheder og organisationer",
    slug: "virksomheder",
    title: "Personligt lederskab",
    short:
      "Et udviklingsforløb om at forstå sig selv, finde sin retning og omsætte personlige værdier til handling, kommunikation og samarbejde.",
    format: "3 workshops á 3–4 timer",
    detail: {
      intro:
        "Et udviklingsforløb for mennesker, der ønsker at forstå sig selv bedre, stå stærkere i det, der er vigtigt for dem, og få konkrete redskaber til hverdagen – på arbejdet og i livet generelt.",
      sections: [
        {
          heading: "Workshop 1: Forstå dig selv",
          subheading: "Retning og værdier",
          lead:
            "Vi undersøger, hvad der driver den enkelte, hvilke værdier der er vigtige, og hvordan vores mønstre, overbevisninger og reaktioner påvirker den måde, vi handler og træffer beslutninger på.",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "Hvad sker der med mig, når noget er på spil?",
            "Personlige værdier og motivation",
            "Styrker, mønstre og overbevisninger",
            "Personlig retning og indre kompas",
          ],
        },
        {
          heading: "Workshop 2: Vær dig selv",
          subheading: "Fra værdier til handling",
          lead:
            "Vi arbejder med afstanden mellem det, vi gerne vil stå for, og det, vi faktisk gør – særligt når noget bliver svært, uforudsigeligt eller vigtigt.",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "At omsætte værdier til konkret adfærd",
            "At rumme tvivl, usikkerhed og fejl",
            "Fokus på det, der kan påvirkes",
            "Bevidste valg frem for automatiske reaktioner",
          ],
        },
        {
          heading: "Workshop 3: Udtryk dig selv",
          subheading: "Når det ydre bliver et udtryk for det indre",
          lead:
            "Vi omsætter den indre klarhed til den måde, den enkelte kommunikerer, samarbejder og træder frem – i mødelokalet, i teamet og i livet generelt.",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "Autenticitet og gennemslagskraft",
            "Kommunikation og fremtoning",
            "Feedback og relationer",
            "En personlig gameplan for hverdagen",
          ],
        },
      ],
      closing: [],
      outcome:
        "Større selvindsigt, en tydeligere personlig retning og konkrete redskaber til at handle mere bevidst – både på arbejdet og i livet generelt.",
      note: null,
    },
  },
  {
    label: "Sport",
    slug: "sport",
    title: "Stå stærkt under pres",
    short:
      "Et praksisnært forløb for atleter og teams, der ønsker at forstå deres reaktioner, håndtere præstationspres og stå stærkere i både medgang og modgang.",
    format: "4 workshops á 90–120 minutter",
    detail: {
      intro:
        "Et udviklingsforløb for atleter og teams, der ønsker at forstå sig selv bedre, opbygge et stærkere mentalt fundament og få konkrete redskaber til træning, kamp og hverdagsliv.",
      sections: [
        {
          heading: "Workshop 1: Forstå dig selv under pres",
          bullets: [
            "Hvad sker der med mig, når noget er på spil?",
            "Tanker, følelser, krop og adfærd",
            "Personlige triggere og automatiske reaktioner",
            "Bevidsthed om egne mentale mønstre",
          ],
        },
        {
          heading: "Workshop 2: Mennesket bag præstationen",
          bullets: [
            "Identitet og selvværd",
            "Hvem er jeg, når jeg ikke præsterer?",
            "Værdier og personlig retning",
            "At adskille resultatet fra værdien som menneske",
          ],
        },
        {
          heading: "Workshop 3: Mental styrke i praksis",
          bullets: [
            "At rumme nervøsitet, usikkerhed og fejl",
            "Fokus på det, der kan påvirkes",
            "At vende tilbage til nuet",
            "Konkrete mentale og kropslige redskaber",
          ],
        },
        {
          heading: "Workshop 4: Min personlige gameplan",
          bullets: [
            "Hvem ønsker jeg at være som spiller og menneske?",
            "Hvordan vil jeg reagere, når noget bliver svært?",
            "Personlige fokuspunkter før, under og efter præstation",
            "Overførsel til træning, kamp og hverdagsliv",
          ],
        },
      ],
      closing: [],
      outcome:
        "Større mental bevidsthed, et mere stabilt indre fundament og konkrete redskaber til at navigere i krav, forventninger, fejl og modgang.",
      note: "Forløbet kan gennemføres for et hold, en mindre gruppe eller individuelt.",
    },
  },
  {
    label: "Kreative miljøer",
    slug: "kreative-miljoeer",
    title: "Hvil i dig selv – både på og bag scenen",
    short:
      "Et udviklingsforløb for kunstnere og performere, der ønsker et stærkere mentalt fundament og mere mod til at stå ved sig selv og deres udtryk.",
    format: "3 workshops á 2–3 timer",
    detail: {
      intro:
        "Et udviklingsforløb for kunstnere og performere, der skal vise sig frem, skabe noget personligt og navigere i en omverden, der vurderer, reagerer og stiller forventninger.",
      sections: [
        {
          heading: "Workshop 1: Dit mentale fundament",
          subheading: "Hvem er du bag præstationen?",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "Identitet, selvværd og autenticitet",
            "Personlige værdier og kunstnerisk retning",
            "Ydre forventninger og vurderinger",
            "Egne mønstre og overbevisninger",
          ],
        },
        {
          heading: "Workshop 2: Modet til at vise dig",
          subheading: "Når synlighed føles sårbart",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "Frygt for fejl, afvisning og bedømmelse",
            "At handle, selvom usikkerheden er til stede",
            "At stå ved sig selv og sit udtryk",
            "Mental styrke og grounding",
          ],
        },
        {
          heading: "Workshop 3: Fra det indre til det ydre",
          subheading: "Autentisk gennemslagskraft",
          bulletsLabel: "Vi arbejder blandt andet med:",
          bullets: [
            "Fremtoning, stemme og kropssprog",
            "Nærvær i mødet med et publikum",
            "At kommunikere, hvem man er",
            "En personlig gameplan for præstation og synlighed",
          ],
        },
      ],
      closing: [],
      outcome:
        "Et stærkere mentalt fundament, større bevidsthed om eget udtryk og mere mod til at stå frem som den, man er.",
      note: "Forløbet kan tilpasses den enkelte kunstner, en gruppe eller et kreativt team og kan udvides med relevante faglige samarbejdspartnere.",
    },
  },
  {
    label: "Unge og uddannelse",
    slug: "unge-og-uddannelse",
    title: "Selvudvikling og kommunikation",
    short:
      "Et involverende undervisningsforløb, der hjælper unge med at forstå sig selv og andre, finde retning og handle på det, der er vigtigt for dem.",
    format: "3 temadage eller et længere undervisningsforløb",
    detail: {
      intro:
        "Et praksisnært undervisningsforløb, der hjælper unge med at forstå sig selv og andre, finde retning og få mod til at stå ved og handle på det, der er vigtigt for dem.",
      sections: [
        {
          heading: "Del 1: Forstå dig selv",
          bullets: [
            "Tanker, følelser og adfærd",
            "Historie, erfaringer og overbevisninger",
            "Selvbevidsthed og selvværd",
            "Mønstre, der hjælper eller begrænser os",
          ],
        },
        {
          heading: "Del 2: Vær dig selv",
          bullets: [
            "Autenticitet og værdier",
            "Retning og motivation",
            "Mod og mental styrke",
            "At handle på det, der er vigtigt",
          ],
        },
        {
          heading: "Del 3: Vær med andre og træd frem",
          bullets: [
            "Kommunikation og aktiv lytning",
            "Kropssprog, stemme og fremtoning",
            "Feedback og relationer",
            "Modet til at tage plads og vise sig frem",
          ],
        },
      ],
      closing: [
        "Undervisningen veksler mellem korte faglige oplæg, refleksion, samtaler, konkrete øvelser og praktisk afprøvning.",
      ],
      outcome:
        "Eleverne får større forståelse for sig selv og andre, et tydeligere indre kompas og konkrete redskaber til at navigere i valg, relationer og situationer, hvor der er noget på spil.",
      note: null,
    },
  },
];

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-5 w-5">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const ProgramModal = ({ program, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const { detail } = program;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center sm:p-6"
      data-testid="preview-forloeb-modal"
    >
      <div
        className="absolute inset-0 bg-[#111827]/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="preview-forloeb-modal-backdrop"
      />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-white sm:max-h-[86vh] sm:rounded-3xl preview-shadow-lift">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-black/[0.06] bg-white px-6 py-5 sm:px-8">
          <div>
            <span className="eyebrow block text-[11px] font-semibold text-[#852936]">{program.label}</span>
            <h3 className="mt-1.5 font-display text-2xl font-semibold leading-tight text-[#111827] sm:text-3xl">
              {program.title}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Luk"
            className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F4F1EC] text-[#111827]/60 transition-colors hover:bg-[#852936]/10 hover:text-[#852936]"
            data-testid="preview-forloeb-modal-close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">
          <span className="inline-flex w-fit rounded-full bg-[#852936]/[0.08] px-3.5 py-1.5 text-[13px] font-semibold text-[#6E1F2B]">
            {program.format}
          </span>

          <p className="mt-5 text-[16px] leading-relaxed text-[#111827]/70">{detail.intro}</p>

          <div className="mt-7 space-y-6">
            {detail.sections.map((s) => (
              <div key={s.heading} className="rounded-2xl border border-black/[0.06] bg-[#FBFBF9] p-5 sm:p-6">
                <h4 className="font-display text-lg font-semibold leading-snug text-[#111827]">{s.heading}</h4>
                {s.subheading && (
                  <p className="mt-1 font-display text-[16px] font-normal italic text-[#852936]">{s.subheading}</p>
                )}
                {s.lead && <p className="mt-3 text-[15px] leading-relaxed text-[#111827]/70">{s.lead}</p>}
                {s.bulletsLabel && (
                  <p className="mt-3 text-[15px] font-semibold text-[#111827]">{s.bulletsLabel}</p>
                )}
                <ul className="mt-3 space-y-2 text-[15px] leading-relaxed text-[#111827]/70">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#852936]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {detail.closing.length > 0 && (
            <div className="mt-6 space-y-3 text-[15px] leading-relaxed text-[#111827]/70">
              {detail.closing.map((p, j) => (
                <p key={j}>{p}</p>
              ))}
            </div>
          )}

          <div className="mt-6 rounded-2xl bg-[#A9B5A0]/15 p-5 sm:p-6">
            <span className="eyebrow block text-[11px] font-semibold text-[#111827]/50">Udbytte</span>
            <p className="mt-2 text-[15px] leading-relaxed text-[#111827]/80">{detail.outcome}</p>
          </div>

          {detail.note && (
            <p className="mt-5 text-[15px] italic leading-relaxed text-[#111827]/60">{detail.note}</p>
          )}

          <button
            onClick={onClose}
            className="mt-8 inline-flex items-center rounded-full border border-black/10 px-6 py-3 text-[15px] font-medium text-[#111827] transition-colors duration-300 hover:border-black/30"
            data-testid="preview-forloeb-modal-back"
          >
            Tilbage til oversigten
          </button>
        </div>
      </div>
    </div>
  );
};

const PreviewForloeb = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get("forloeb");
  const openIndex = programs.findIndex((p) => p.slug === slug);
  const openProgram = (i) => setSearchParams({ forloeb: programs[i].slug });
  const closeProgram = () => setSearchParams({});

  return (
    <>
      <div className="mt-10 grid gap-5 md:grid-cols-2" data-testid="preview-forloeb-grid">
        {programs.map((p, i) => (
          <Reveal key={p.title} delay={(i % 2) * 100}>
            <div
              onClick={() => openProgram(i)}
              className="group flex h-full cursor-pointer flex-col rounded-3xl border border-black/[0.06] bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 sm:p-8 preview-shadow-soft hover:preview-shadow-lift"
              data-testid={`preview-forloeb-card-${i}`}
            >
              <span className="eyebrow block text-[11px] font-semibold text-[#852936]">{p.label}</span>
              <h4 className="mt-3 font-display text-xl font-semibold leading-snug text-[#111827] sm:text-2xl">
                {p.title}
              </h4>
              <p className="mt-3 flex-1 text-[15px] leading-relaxed text-[#111827]/65 sm:text-[16px]">{p.short}</p>
              <p className="mt-4 text-[13px] font-semibold text-[#111827]/50">
                <span className="eyebrow text-[11px] text-[#111827]/40">Format · </span>
                {p.format}
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openProgram(i);
                }}
                className="group/btn mt-5 inline-flex w-fit items-center gap-2 text-[15px] font-semibold text-[#852936] transition-colors hover:text-[#6E1F2B]"
                data-testid={`preview-forloeb-open-${i}`}
              >
                Se eksempel på forløb
                <ArrowIcon />
              </button>
            </div>
          </Reveal>
        ))}
      </div>

      {/* CTA */}
      <Reveal delay={80}>
        <div
          className="mt-10 rounded-3xl border border-black/[0.06] bg-white p-7 sm:p-10 preview-shadow-soft"
          data-testid="preview-forloeb-cta"
        >
          <h4 className="mt-5 font-display text-2xl font-semibold leading-snug text-[#111827] sm:text-3xl">
            Skal vi sammensætte et forløb til jer?
          </h4>
          <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-[#111827]/70 sm:text-[17px]">
            Eksemplerne er ikke faste pakkeløsninger. Sammen finder vi det format og indhold, der passer
            til jeres mennesker, virkelighed og ambitioner.
          </p>
          <Link
            to="/kontakt"
            className="mt-7 inline-flex items-center rounded-full bg-[#852936] px-8 py-4 text-base font-medium text-white transition-transform duration-300 hover:scale-[1.04] preview-shadow-soft"
            data-testid="preview-forloeb-cta-button"
          >
            Lad os tale sammen
          </Link>
        </div>
      </Reveal>

      {openIndex >= 0 && (
        <ProgramModal program={programs[openIndex]} onClose={closeProgram} />
      )}
    </>
  );
};

export default PreviewForloeb;
